/** XeAgents.ai **/
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Mail,
  ArrowRight,
  AlertCircle,
  Loader2,
  KeyRound,
  Eye,
  EyeOff,
  Mic2,
  Brain,
  Phone,
  Shield,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { useBranding } from "@/components/BrandingProvider";
import { AuthStorage } from "@/lib/auth-storage";
import { LoginMfaStep, isMfaChallenge, type LoginMfaChallenge } from "@/components/auth/LoginMfaStep";
import { AILoadingAnimation } from "@/components/landing/AILoadingAnimation";
import { Link } from "wouter";
import { ThemeToggle } from "@/components/ThemeToggle";

type AuthView = "login" | "register" | "forgot-password";

// Animated gradient orb component
function GradientOrb({
  className,
  delay = 0,
}: {
  className: string;
  delay?: number;
}) {
  return (
    <motion.div
      className={className}
      animate={{
        scale: [1, 1.3, 1],
        opacity: [0.4, 0.7, 0.4],
        y: [0, -20, 0],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
        delay,
      }}
    />
  );
}

// Floating particle component
function FloatingParticle({ index }: { index: number }) {
  const positions = [
    { left: "10%", top: "15%" },
    { left: "25%", top: "60%" },
    { left: "40%", top: "25%" },
    { left: "60%", top: "70%" },
    { left: "75%", top: "35%" },
    { left: "85%", top: "55%" },
    { left: "15%", top: "80%" },
    { left: "50%", top: "45%" },
  ];
  const pos = positions[index % positions.length];

  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full bg-white/40"
      style={{ left: pos.left, top: pos.top }}
      animate={{
        y: [0, -40, 0],
        x: [0, 10, 0],
        opacity: [0.2, 0.6, 0.2],
        scale: [1, 1.2, 1],
      }}
      transition={{
        duration: 5 + index,
        repeat: Infinity,
        ease: "easeInOut",
        delay: index * 0.7,
      }}
    />
  );
}

// Feature item for hero side with stagger support
function FeatureItem({
  icon: Icon,
  text,
  index = 0,
}: {
  icon: React.ElementType;
  text: string;
  index?: number;
}) {
  return (
    <motion.div
      className="flex items-center gap-3 text-white"
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.6 + index * 0.15 }}
    >
      <motion.div
        className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 backdrop-blur-sm border border-white/10"
        whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.3)" }}
      >
        <Icon className="h-5 w-5" />
      </motion.div>
      <span className="text-sm font-medium">{text}</span>
    </motion.div>
  );
}

export default function SignIn() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { branding, currentLogo } = useBranding();

  // Auth state
  const [isLoading, setIsLoading] = useState(false);
  const [authView, setAuthView] = useState<AuthView>("login");
  const [showPassword, setShowPassword] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Set when the server wants an emailed code after a correct email + password
  const [mfaChallenge, setMfaChallenge] = useState<LoginMfaChallenge | null>(null);

  // Loading animation state
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [loadingUserName, setLoadingUserName] = useState<string | undefined>();
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);

  // Form states
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: "",
  });

  const [resetPasswordForm, setResetPasswordForm] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });

  // OTP state for forgot password
  const [forgotPasswordStep, setForgotPasswordStep] = useState<
    "email" | "otp" | "new-password"
  >("email");
  const [otpCode, setOtpCode] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);

  // OTP timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (otpTimer === 0 && forgotPasswordStep === "otp") {
      setCanResendOtp(true);
    }
  }, [otpTimer, forgotPasswordStep]);

  // Reset logo error when logo changes
  useEffect(() => {
    setLogoError(false);
  }, [currentLogo]);

  const handleLoadingComplete = () => {
    if (pendingRedirect) {
      window.location.href = pendingRedirect;
    }
  };

  const showLoading = (userName: string, redirectUrl: string) => {
    setLoadingUserName(userName);
    setPendingRedirect(redirectUrl);
    setShowLoadingAnimation(true);
  };

  // Post-login handling shared by the password-only path and the email-code (MFA) path
  const completeLogin = (data: any) => {
    AuthStorage.setAuthData(data.token, data.user);

    // Handle mandatory password change (admin-created users with temp password)
    if (data.mustChangePassword) {
      toast({
        title: "Password Change Required",
        description: "Please change your temporary password to continue.",
      });
      showLoading(data.user.name, "/app/settings?requirePasswordChange=true");
      return;
    }

    toast({
      title: t("auth.welcomeBack"),
      description: t("auth.loggedInAs", { name: data.user.name }),
    });

    const redirectUrl = data.user.isSuperAdmin === true ? "/admin" : "/app";
    showLoading(data.user.name, redirectUrl);
  };

  const reportLoginError = (error: any) => {
    const errorMessage = error.message || "Login failed";

    toast({
      title: t("auth.loginFailed"),
      description: errorMessage,
      variant: "destructive",
    });
  };

  // Login handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Correct password, but a code emailed to the user is still required
      if (isMfaChallenge(data)) {
        setMfaChallenge(data);
        return;
      }

      completeLogin(data);
    } catch (error: any) {
      reportLoginError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSuccess = (data: any) => {
    try {
      completeLogin(data);
    } catch (error: any) {
      // Malformed success payload - fall back to the password form rather than leave the step stuck
      setMfaChallenge(null);
      reportLoginError(error);
    }
  };

  const handleMfaBack = (message?: string) => {
    setMfaChallenge(null);
    if (message) {
      toast({
        title: t("auth.mfa.signInAgain"),
        description: message,
        variant: "destructive",
      });
    }
  };

  // Forgot password - send OTP
  const handleForgotPasswordSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send verification code");
      }

      toast({
        title: "Code sent!",
        description: "If an account exists, check your email for the code",
      });

      setResetPasswordForm({
        ...resetPasswordForm,
        email: forgotPasswordForm.email,
      });
      setForgotPasswordStep("otp");
      setOtpTimer(300);
      setCanResendOtp(false);
      setOtpCode("");
    } catch (error: any) {
      toast({
        title: "Failed to send code",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password - resend OTP
  const handleForgotPasswordResendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/forgot-password/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to resend verification code");
      }

      toast({
        title: "Code resent",
        description: "A new verification code has been sent",
      });

      setOtpTimer(300);
      setCanResendOtp(false);
      setOtpCode("");
    } catch (error: any) {
      toast({
        title: "Failed to resend",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password - verify OTP
  const handleForgotPasswordVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordForm.email,
          otpCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Invalid verification code");
      }

      toast({
        title: "Verified!",
        description: "Now set your new password",
      });

      setForgotPasswordStep("new-password");
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive",
      });
      return;
    }

    if (resetPasswordForm.newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: resetPasswordForm.email,
          newPassword: resetPasswordForm.newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      toast({
        title: "Password reset!",
        description: "You can now sign in with your new password",
      });

      setAuthView("login");
      setForgotPasswordStep("email");
      setLoginForm({ email: resetPasswordForm.email, password: "" });
    } catch (error: any) {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render forgot password flow
  const renderForgotPassword = () => {
    if (forgotPasswordStep === "email") {
      return (
        <form onSubmit={handleForgotPasswordSendOTP} className="space-y-4">
          <div className="text-center space-y-3 mb-6">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <KeyRound className="h-7 w-7 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">Forgot password?</h3>
              <p className="text-sm text-muted-foreground">
                Enter your email and we'll send you a reset code
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              placeholder="you@company.com"
              value={forgotPasswordForm.email}
              onChange={(e) =>
                setForgotPasswordForm({ email: e.target.value })
              }
              required
              className="h-11"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-11"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Send Reset Code
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => setAuthView("login")}
          >
            Back to Sign In
          </Button>
        </form>
      );
    }

    if (forgotPasswordStep === "otp") {
      return (
        <form onSubmit={handleForgotPasswordVerifyOTP} className="space-y-6">
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <Mail className="h-7 w-7 text-slate-600 dark:text-slate-300" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">Check your email</h3>
              <p className="text-sm text-muted-foreground">
                We sent a code to{" "}
                <strong className="text-foreground">
                  {forgotPasswordForm.email}
                </strong>
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {otpTimer > 0 && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>
                Code expires in {Math.floor(otpTimer / 60)}:
                {String(otpTimer % 60).padStart(2, "0")}
              </span>
            </div>
          )}

          <div className="space-y-3">
            <Button
              type="submit"
              className="w-full h-11"
              disabled={isLoading || otpCode.length !== 6}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Verify Code"
              )}
            </Button>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setForgotPasswordStep("email")}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleForgotPasswordResendOTP}
                disabled={isLoading || !canResendOtp}
              >
                {canResendOtp ? "Resend Code" : `Resend in ${otpTimer}s`}
              </Button>
            </div>
          </div>
        </form>
      );
    }

    if (forgotPasswordStep === "new-password") {
      return (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="text-center space-y-3 mb-6">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <KeyRound className="h-7 w-7 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg">Create new password</h3>
              <p className="text-sm text-muted-foreground">
                Enter your new password below
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                value={resetPasswordForm.newPassword}
                onChange={(e) =>
                  setResetPasswordForm({
                    ...resetPasswordForm,
                    newPassword: e.target.value,
                  })
                }
                required
                minLength={6}
                className="h-11 pr-12"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                value={resetPasswordForm.confirmPassword}
                onChange={(e) =>
                  setResetPasswordForm({
                    ...resetPasswordForm,
                    confirmPassword: e.target.value,
                  })
                }
                required
                minLength={6}
                className="h-11 pr-12"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              "Reset Password"
            )}
          </Button>
        </form>
      );
    }

    return null;
  };

  return (
    <>
      {/* Loading Animation */}
      <AILoadingAnimation
        isVisible={showLoadingAnimation}
        onComplete={handleLoadingComplete}
        userName={loadingUserName}
      />

      <div className="min-h-screen flex">
        {/* Hero Side - Hidden on mobile */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-400 overflow-hidden">
          {/* Depth overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-orange-600/30 via-transparent to-white/10" />

          {/* Animated gradient orbs */}
          <GradientOrb
            className="absolute top-10 left-10 w-72 h-72 rounded-full bg-orange-400/50 blur-3xl"
            delay={0}
          />
          <GradientOrb
            className="absolute bottom-32 right-5 w-96 h-96 rounded-full bg-yellow-200/60 blur-3xl"
            delay={2}
          />
          <GradientOrb
            className="absolute top-1/3 right-1/4 w-56 h-56 rounded-full bg-white/30 blur-3xl"
            delay={3}
          />
          {/* Darker orb in bottom left for depth */}
          <GradientOrb
            className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-orange-700/40 blur-3xl"
            delay={1}
          />

          {/* Floating particles */}
          {[...Array(8)].map((_, i) => (
            <FloatingParticle key={i} index={i} />
          ))}

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Logo + App Name with glow */}
              <motion.div
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src="/images/branding/voiceprologo.svg"
                  alt="VoicePro logo"
                  className="h-24 w-auto max-w-[360px] object-contain drop-shadow-lg"
                />
              </motion.div>

              {/* Tagline */}
              <div className="space-y-4">
                <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                  Transform Your
                  <br />
                  <motion.span
                    className="text-amber-200 inline-block"
                    animate={{
                      textShadow: [
                        "0 0 0px rgba(251, 191, 36, 0)",
                        "0 0 20px rgba(251, 191, 36, 0.5)",
                        "0 0 0px rgba(251, 191, 36, 0)"
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    Voice Operations
                  </motion.span>
                </h1>
                <p className="text-lg text-white/90 max-w-md drop-shadow">
                  {branding.app_tagline ||
                    "AI-powered voice agents for automated calling, lead qualification, and 24/7 customer support."}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-4 pt-4">
                <FeatureItem icon={Mic2} text="Natural AI Voice Conversations" index={0} />
                <FeatureItem icon={Brain} text="Smart Lead Qualification" index={1} />
                <FeatureItem icon={Phone} text="Automated Outbound Calling" index={2} />
                <FeatureItem icon={Shield} text="Enterprise-Grade Security" index={3} />
              </div>

              {/* Trust indicator */}
              <motion.div
                className="flex items-center gap-3 pt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <motion.div
                      key={i}
                      className="w-9 h-9 rounded-full bg-white/30 border-2 border-white/40 flex items-center justify-center text-xs text-white font-semibold shadow-lg"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.3 + i * 0.1 }}
                    >
                      {String.fromCharCode(64 + i)}
                    </motion.div>
                  ))}
                </div>
                <span className="text-sm text-white font-medium ml-2 drop-shadow">
                  Trusted by 500+ businesses
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Auth Form Side */}
        <div className="w-full lg:w-1/2 flex flex-col bg-background">
          {/* Top bar with theme toggle */}
          <div className="flex items-center justify-between p-4 lg:p-6">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-2">
              {currentLogo && !logoError ? (
                <img
                  src={currentLogo}
                  alt={branding.app_name}
                  className="h-12 w-auto max-w-[140px] object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <Sparkles className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
              <span className="font-semibold">{branding.app_name}</span>
            </div>
            <div className="hidden lg:block" />
            <ThemeToggle />
          </div>

          {/* Form container */}
          <div className="flex-1 flex items-center justify-center px-4 sm:px-8 lg:px-12 xl:px-20 pb-8">
            <div className="w-full max-w-md">
              {/* Header (the email-code step brings its own heading) */}
              {!mfaChallenge && (
                <motion.div
                  className="mb-8"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-2xl font-bold">
                    {authView === "forgot-password"
                      ? "Reset password"
                      : "Welcome back"}
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    {authView === "forgot-password"
                      ? "Enter your email to receive a reset code"
                      : "Sign in to manage your AI voice agents"}
                  </p>
                </motion.div>
              )}

              {mfaChallenge ? (
                <LoginMfaStep
                  challenge={mfaChallenge}
                  onSuccess={handleMfaSuccess}
                  onBack={handleMfaBack}
                  submitButtonClassName="h-11"
                />
              ) : authView === "forgot-password" ? (
                renderForgotPassword()
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@company.com"
                      value={loginForm.email}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, email: e.target.value })
                      }
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() => {
                          setAuthView("forgot-password");
                          setForgotPasswordForm({ email: loginForm.email });
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        value={loginForm.password}
                        onChange={(e) =>
                          setLoginForm({
                            ...loginForm,
                            password: e.target.value,
                          })
                        }
                        required
                        className="h-11 pr-12"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Footer link */}
              <div className="mt-8 text-center text-sm text-muted-foreground">
                <Link href="/" className="hover:text-primary hover:underline">
                  Back to home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
